import { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  LayoutDashboard,
  Building2,
  Boxes,
  Settings,
  Megaphone,
  Crown,
  LogOut,
  Menu,
  X,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/businesses", label: "Businesses", icon: Building2, testid: "nav-businesses" },
  { to: "/marketing", label: "Marketing", icon: Megaphone, testid: "nav-marketing" },
  { to: "/shared-services", label: "Shared Services", icon: Boxes, testid: "nav-shared-services" },
  { to: "/administration", label: "Administration", icon: Settings, testid: "nav-administration" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-6 h-16 border-b border-gray-200">
        <div className="h-9 w-9 rounded-lg bg-[#581C87] flex items-center justify-center shrink-0">
          <Crown className="h-5 w-5 text-white" />
        </div>
        <div className="leading-tight">
          <p className="font-heading font-bold text-sm tracking-tight">KISSED</p>
          <p className="text-[10px] uppercase tracking-widest text-gray-400">Ecosystem OS</p>
        </div>
      </div>

      <nav className="flex-1 px-3 py-6 space-y-1">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setOpen(false)}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#F3E8FF] text-[#6B21A8]"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`
              }
            >
              <Icon className="h-[18px] w-[18px]" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-3 py-4 border-t border-gray-200">
        <div className="px-3 py-2 mb-2">
          <p className="text-xs text-gray-400 uppercase tracking-widest">Signed in</p>
          <p className="text-sm font-medium truncate" data-testid="current-user-email">
            {user?.email}
          </p>
        </div>
        <Button
          variant="ghost"
          onClick={logout}
          className="w-full justify-start text-gray-600 hover:text-red-600 hover:bg-red-50"
          data-testid="logout-button"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-white border-r border-gray-200">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 h-16 flex items-center justify-between px-6 border-b border-gray-200 bg-white/80 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <span className="font-heading font-semibold text-gray-900 capitalize">
              {NAV.find((n) => n.to === location.pathname)?.label ||
                (location.pathname === "/" ? "Dashboard" : "")}
            </span>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs text-gray-400 uppercase tracking-widest">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            Control Plane
          </div>
        </header>

        <main className="p-6 md:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
